import { Check } from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  {
    n: "01",
    title: "AI Feature Security Review",
    tagline:
      "Your front-door engagement. Ship the AI feature with the security, privacy, and compliance work already done.",
    desc:
      "A focused pre-launch or post-launch review of chatbots, copilots, RAG search, AI agents, and AI-enabled workflows. We assess data exposure, tenant isolation, authorization, prompt injection, unsafe actions, privacy/logging, and enterprise-readiness gaps.",
    valueWrap:
      "Your engineering, security, and executive teams launch the feature with documented evidence the risks were assessed, remediated, and validated — before customers, auditors, or enterprise buyers ask.",
    items: [
      "AI feature threat model",
      "Technical findings report",
      "Executive risk summary",
      "Prioritized remediation roadmap",
      "Validation and evidence package",
    ],
    price: "Scoped fixed-fee engagement. Proposal within 48 hours.",
  },
  {
    n: "02",
    title: "AI Application Security Assessment",
    tagline:
      "Your board stops asking the AI security question because the answer is already documented.",
    desc:
      "A focused review of the AI feature, application layer, data access, authorization, prompts, retrieval paths, logging, privacy, and compliance evidence. You leave with a prioritized roadmap your engineering, security, and executive teams can execute against.",
    valueWrap:
      "Your engineering, compliance, and legal teams operate from a single source of truth about your AI application risk. Every diligence question is answered with documentation, not a scramble.",
    items: [
      "AI, application, API, and agentic workflow review",
      "Privacy, consent, and data-flow controls review",
      "Governance mapping: HIPAA · SOC 2 · HITRUST · CMMC · NIST AI RMF",
      "Vendor and model risk register",
      "Prioritized remediation roadmap and executive briefing",
    ],
    price: "Scoped to your environment",
  },
  {
    n: "03",
    title: "AI Red Team",
    desc:
      "Adversarial simulation across your full AI stack, prompt injection, jailbreaks, data exfiltration, model inversion, multi-turn orchestration, and tool-use exploitation. Findings are mapped to MITRE ATLAS and OWASP, with an engineered remediation path delivered alongside the report.",
    valueWrap:
      "Your leadership team makes AI deployment decisions based on proven evidence of what is exploitable, not on assumption, and your legal and compliance teams have the documentation they need before anyone asks for it.",
    items: [
      "Full red team findings report with MITRE ATLAS and OWASP LLM Top 10 mapping",
      "Tool-use and cross-system orchestration testing",
      "Engineering and regulatory context per finding",
      "Prioritized remediation roadmap and pre-launch governance package",
    ],
    price: "Scoped to your attack surface",
  },
  {
    n: "04",
    title: "Application Security",
    tagline:
      "Your application layer is no longer the unreviewed surface that puts your AI program at risk.",
    desc:
      "AI security stands on application security. We integrate secure SDLC, API security, authorization controls, threat modeling, and vulnerability management into your engineering organization, so the surface AI depends on is reviewed, instrumented, and validated before production.",
    valueWrap:
      "Every gap in your application security is closed, documented, and validated, so the foundation beneath your AI program is one your security team can defend and your auditors can verify.",
    items: [
      "Threat models mapped to STRIDE with risk-rated findings per component",
      "SAST and SCA coverage with enforcement gates in CI/CD",
      "API security assessment with authorization and authentication gap analysis",
      "Cloud application security and software supply-chain review",
      "Penetration testing governance with vendor management and SLAs",
      "Vulnerability management program with SLA tiers, Jira integration, and executive reporting",
      "Secrets management architecture review and implementation guidance",
    ],
    frameworks: "OWASP · STRIDE · NIST · CWE",
    closing: "Every finding ships with a fix, not just a flag.",
    price: "Scoped to your application footprint",
  },
  {
    n: "05",
    title: "Privacy & Consent Enforcement",
    tagline: "Privacy is an engineering surface, not a configuration screen.",
    desc:
      "We design, implement, and validate the privacy and consent layer beneath your digital and AI footprint, tag governance, consent enforcement, regional signal handling, and runtime consent validation. The deliverable is a verified privacy implementation, with the evidence to back it.",
    valueWrap:
      "When a regulator or litigant asks how you handle consent, your legal team produces documentation, not explanations. The evidence trail exists before the question arrives.",
    items: [
      "Pre and post-consent tracking audit across GB and US-CA regions",
      "Google Consent Mode v2 and GPC implementation, validated in production",
      "Cookie deletion on opt-out and regional CCPA/CPRA/GDPR verification",
      "OneTrust, GTM, and tag management remediation with documented change log",
      "Implementation record mapped to Privaini or equivalent scan findings",
    ],
    frameworks: "CCPA · CPRA · GDPR · NIST Privacy Framework",
    closing: "We engineer the privacy layer and validate it in production.",
    price: "Scoped to your digital surface",
  },
  {
    n: "06",
    title: "Remediation and AI Assurance",
    desc:
      "Engineering execution after the review. Fix implementation, runtime protection, controlled rollouts, and continuous governance, operated as a documented program with monthly or quarterly validation of AI features, remediation status, risk posture, and evidence readiness.",
    valueWrap:
      "Your AI security posture is never a point-in-time snapshot. Every fix holds, every audit is answered, and every new AI feature is validated before it creates exposure.",
    items: [
      "Fix implementation, validation, and regression testing",
      "Runtime protection, monitoring, and governance deployment",
      "Monthly posture report and quarterly AI feature re-assessment",
      "Regulatory and threat-landscape change monitoring",
      "Named engagement lead with documented backup",
    ],
    price: "Monthly retainer. Scoped to your environment",
  },
  {
    n: "07",
    title: "Safe AI Adoption Program",
    tagline: "The expansion path once the security foundation is validated.",
    desc:
      "Once the AI feature security work is in place, we partner with engineering and product to operationalize safe expansion — new features, new agents, new tool integrations, and new data sources — without losing the security posture you just built.",
    valueWrap:
      "Your organization adopts AI broadly with the assurance that every new surface is reviewed, governed, and documented from day one.",
    items: [
      "AI feature intake and threat-modeling workflow",
      "Authorization design for agents, tools, and downstream actions",
      "Human approval gates, escalation paths, and workflow isolation",
      "Runtime monitoring, audit logging, and observability",
      "Secure integration patterns for enterprise APIs and SaaS",
    ],
    closing: "Autonomous AI is an engineering surface. We govern it like one.",
    price: "Scoped to your AI roadmap",
  },
];

export default function ArqServices() {
  return (
    <section id="services" className="bg-arq-bg py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center max-w-[820px] mx-auto mb-16">
          <div className="arq-label mb-6">What We Do</div>
          <h2 className="font-display font-semibold text-arq-text mb-5" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            Start With an AI Feature Security Review. Expand as Risk Demands.
          </h2>
          <p className="text-arq-muted text-[17px] leading-[1.75]">
            Most teams start with the AI Feature Security Review. The remaining engagements are
            how ArqeraAI executes, validates, and operates the roadmap it produces.
          </p>

        </div>

        {/* Start-here: Assessment (full width, elevated) */}
        <div className="mb-10">
          <div className="arq-card p-8 md:p-10 flex flex-col border-arq-blue/40 ring-1 ring-arq-blue/30 shadow-[0_0_40px_-12px_hsl(var(--arq-blue)/0.35)] relative">
            <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-arq-blue text-white text-[11px] font-semibold uppercase tracking-[0.08em] shadow-md">
              Start Here
            </div>
            <div className="font-mono-tech text-[12px] text-arq-blue mb-3 tracking-[0.08em]">{services[0].n}</div>
            <h3 className="font-display text-[22px] md:text-[24px] font-semibold text-arq-text mb-2">{services[0].title}</h3>
            {services[0].tagline && (
              <p className="text-arq-teal text-[14px] font-medium italic mb-3">{services[0].tagline}</p>
            )}
            <p className="text-arq-muted text-[15px] leading-[1.7] mb-6 max-w-[820px]">{services[0].desc}</p>
            {services[0].valueWrap && (
              <p className="text-arq-text/90 text-[14px] leading-[1.7] mb-5 max-w-[820px]">{services[0].valueWrap}</p>
            )}
            <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3 mb-6">
              {services[0].items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-[15px] text-arq-text/90">
                  <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "hsl(var(--arq-teal))" }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between gap-4 pt-5 border-t border-arq-border">
              <div className="font-mono-tech text-[12px] text-arq-tertiary">
                {services[0].price}
              </div>
              <Link
                to="/ai-feature-security-review"
                className="text-arq-teal text-[13px] font-medium hover:text-arq-blue underline underline-offset-4 transition-colors whitespace-nowrap"
              >
                See the full offer →
              </Link>
            </div>
          </div>
        </div>

        <div className="text-center mb-8">
          <div className="arq-label text-arq-tertiary">What Comes After The Assessment</div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.slice(1).map((s) => (
            <div key={s.n} className="arq-card p-8 flex flex-col">
              <div className="font-mono-tech text-[12px] text-arq-blue mb-3 tracking-[0.08em]">{s.n}</div>
              <h3 className="font-display text-[20px] font-semibold text-arq-text mb-2">{s.title}</h3>
              {s.tagline && (
                <p className="text-arq-teal text-[14px] font-medium italic mb-3">{s.tagline}</p>
              )}
              <p className="text-arq-muted text-[15px] leading-[1.7] mb-6">{s.desc}</p>
              {s.valueWrap && (
                <p className="text-arq-text/90 text-[14px] leading-[1.7] mb-5">{s.valueWrap}</p>
              )}
              <ul className="space-y-3 mb-6 flex-1">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[15px] text-arq-text/90">
                    <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "hsl(var(--arq-teal))" }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              {s.frameworks && (
                <div className="font-mono-tech text-[11px] text-arq-tertiary tracking-[0.06em] mb-3">
                  FRAMEWORKS: {s.frameworks}
                </div>
              )}
              {s.closing && (
                <p className="text-arq-text text-[14px] italic mb-4">{s.closing}</p>
              )}
              <div className="font-mono-tech text-[12px] text-arq-tertiary pt-5 border-t border-arq-border">
                {s.price}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
